import { postMPData } from 'src/mentor/services'

/**
 * Bulk fetch of the SHORT label shown on a whole day's appointment rows, in ONE network call.
 *
 * The label is role-aware:
 *  - Doctors (consultation events): prefer the documented Diagnosis ('dx'); when there is none,
 *    fall back to the presenting Complaint ('cc'). Reads the light doctor prescription-by-events
 *    endpoint (structured fields only — no images/attachments).
 *  - Therapists (therapy-session events): the therapy type in use ('tx'). Reads the mentor
 *    emr-reports therapy-label endpoint (projects only the therapy-type title).
 *
 * Each fetch returns a Map<eventId, { kind, text }>; events with nothing documented are simply
 * absent from the map (the manager then caches them as "checked, none" so we never refetch).
 */

const MAX_LEN = 64

function shorten(text) {
  return text.length > MAX_LEN ? `${text.slice(0, MAX_LEN - 1)}…` : text
}

function extractItemTitle(item) {
  if (!item) return ''
  const flat = item.value || item.label || item.title || item.name || item.text
  if (typeof flat === 'string' && flat.trim()) return flat.trim()

  const subs = item.subSections || item.subsections || item.sections
  if (Array.isArray(subs)) {
    const titleSub = subs.find(
      (s) => s?.uid === 'diagnosis_title' || s?.id === 'diagnosis_title' || s?.uid === 'complaint_title',
    )
    const sel = titleSub?.selected ?? titleSub?.value ?? titleSub?.label
    if (typeof sel === 'string' && sel.trim()) return sel.trim()
    if (sel && typeof sel === 'object') {
      const v = sel.value || sel.label || sel.title || sel.name
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
  }
  return ''
}

function joinTitles(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const titles = arr.map(extractItemTitle).filter(Boolean)
  if (titles.length === 0) return ''
  let text = titles.slice(0, 2).join(', ')
  if (titles.length > 2) text += ` +${titles.length - 2}`
  return shorten(text)
}

/**
 * Doctor bulk: Diagnosis preferred, Complaint as fallback. Returns Map<eventId, {kind, text}>.
 */
export async function fetchDoctorLabelsForEvents(eventIds) {
  const result = new Map()
  if (!Array.isArray(eventIds) || eventIds.length === 0) return result
  try {
    const resp = await postMPData('/api/secure/doctor/prescription/diagnoses/by-events', { eventIds })
    if (!resp || resp.status !== 200) return result
    const list = resp?.data?.data?.diagnoses || resp?.data?.diagnoses || []
    if (Array.isArray(list)) {
      list.forEach((entry) => {
        if (!entry?.eventId) return
        const dx = joinTitles(entry.diagnosis)
        if (dx) {
          result.set(entry.eventId, { kind: 'dx', text: dx })
          return
        }
        const cc = joinTitles(entry.complaints)
        if (cc) {
          result.set(entry.eventId, { kind: 'cc', text: cc })
        }
      })
    }
  } catch (err) {
    /* graceful: caller treats missing as "none" */
  }
  return result
}

/**
 * Doctor bulk fallback: each patient's MOST RECENT documented Diagnosis (Complaint as fallback)
 * from a previous visit, excluding the given current-day events. Used to back-fill an upcoming
 * appointment card that has no Dx/Cc yet. Returns Map<patientId, {kind, text}>.
 */
export async function fetchDoctorLatestLabelsForPatients(patientIds, excludeEventIds) {
  const result = new Map()
  if (!Array.isArray(patientIds) || patientIds.length === 0) return result
  try {
    const resp = await postMPData('/api/secure/doctor/prescription/diagnoses/latest-by-patients', {
      patientIds,
      excludeEventIds: excludeEventIds || [],
    })
    if (!resp || resp.status !== 200) return result
    const list = resp?.data?.data?.diagnoses || resp?.data?.diagnoses || []
    if (Array.isArray(list)) {
      list.forEach((entry) => {
        if (!entry?.patientId) return
        const dx = joinTitles(entry.diagnosis)
        if (dx) {
          result.set(entry.patientId, { kind: 'dx', text: dx })
          return
        }
        const cc = joinTitles(entry.complaints)
        if (cc) {
          result.set(entry.patientId, { kind: 'cc', text: cc })
        }
      })
    }
  } catch (err) {
    /* graceful: caller treats missing as "none" */
  }
  return result
}

/**
 * Therapist bulk: the therapy type in use. Returns Map<eventId, {kind, text}>.
 */
export async function fetchTherapistLabelsForEvents(eventIds) {
  const result = new Map()
  if (!Array.isArray(eventIds) || eventIds.length === 0) return result
  try {
    const resp = await postMPData(
      '/api/secure/mentor/emr-reports/therapy-report/labels/by-events',
      { eventIds },
    )
    if (!resp || resp.status !== 200) return result
    const list = resp?.data?.data?.labels || resp?.data?.labels || []
    if (Array.isArray(list)) {
      list.forEach((entry) => {
        if (!entry?.eventId) return
        const tx = typeof entry.therapyType === 'string' ? entry.therapyType.trim() : ''
        if (tx) result.set(entry.eventId, { kind: 'tx', text: shorten(tx) })
      })
    }
  } catch (err) {
    /* graceful: caller treats missing as "none" */
  }
  return result
}
